/**
 * MovementsModal — movimentações de UM título. Aberto pela tela Cobrança — Títulos e pela
 * OperationTitleDebtsPage.
 *
 * Mesmas colunas, máscaras e alinhamentos do OperationMovementsModal (que é por operação): é o
 * mesmo histórico, com recorte diferente, e duas tabelas divergentes faziam o usuário reaprender
 * a leitura a cada modal. A única diferença de propósito é a coluna Tipo, que aqui usa o SELO
 * colorido da paleta de pagamento — foi o que se mostrou melhor na tela de Títulos.
 *
 * ⚠️ Inclui os movimentos do COLETOR de recebimento a maior daquele charge. O overpayment não é
 * gravado no título — vira movimento num title_debt separado (status OVERPAID, mesmo charge_id) —
 * e é o backend que junta os dois (get_title_debt_movements). É por isso que a coluna Título vale
 * a pena mesmo num modal de título só: as linhas podem vir de dois title_debts.
 */
import { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import BadgeStatus from '../../../atoms/BadgeStatus';
import {
    statusColors, statusLabels, movementTypeLabels, movementTypeColors,
    businessReferenceLabels,
} from '../constants/settlement';
import { getTitleDebtMovements } from '../services/settlementApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';

interface Props {
    titleDebt: any;
    onClose: () => void;
}

/** Natureza do título: "Recebível" em cessão, "Parcela" em empréstimo, "Garantia" em garantia. */
const referenceColumn = (ref?: string) => businessReferenceLabels[ref || ''] || 'Parcela';

/**
 * Contrato-aditamento, como nas colunas de Contrato das telas de Operações e Títulos. Quem lista
 * já costuma ter o rótulo pronto; sem ele, monta a partir do número + aditamento. O contrato
 * sozinho não identifica: dois aditamentos do mesmo contrato têm títulos diferentes.
 */
const contractLabel = (td: any) => td.contract_label
    || (td.amendment_number != null ? `${td.contract_number}-${td.amendment_number}` : td.contract_number)
    || '-';

/** Contrato-aditamento do título de onde a movimentação veio (igual ao modal de operações). */
const titleLabel = (m: any) => {
    if (m.title_contract_number != null && m.title_amendment_number != null)
        return `${m.title_contract_number}-${m.title_amendment_number}`;
    if (m.title_contract_number != null) return String(m.title_contract_number);
    return m.title_document_title || (m.title_debt_id ? String(m.title_debt_id).slice(0, 8) : '-');
};

const MovementsModal: React.FC<Props> = ({ titleDebt, onClose }) => {
    const [movements, setMovements] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const data = await getTitleDebtMovements(titleDebt.title_debt_id ?? titleDebt.id);
                setMovements(data.data || []);
            } catch { setMovements([]); }
            setLoading(false);
        })();
    }, [titleDebt.title_debt_id, titleDebt.id]);

    /**
     * O status é o MESMO da linha da tabela de onde o modal foi aberto. Quem lista sabe como
     * resolve o próprio status (a tela de Títulos combina dívida e cobrança, e tem o
     * "Paga pós Liq."), então ela manda o selo pronto; sem isso o modal dizia "PAID" cru ao lado
     * de uma linha escrita "Paga pós Liq.", e o usuário via dois estados para o mesmo título.
     */
    const badgeText = titleDebt.status_label || statusLabels[titleDebt.status] || titleDebt.status;
    const badgeColor = titleDebt.status_color || statusColors[titleDebt.status] || 'gray';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Movimentações — {contractLabel(titleDebt)}{' '}
                        ({referenceColumn(titleDebt.business_reference)}{' '}
                        {titleDebt.installment_number || titleDebt.document_title || '-'})
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="mb-4 grid grid-cols-5 gap-4 text-sm bg-gray-50 dark:bg-slate-700 p-3 rounded">
                    <div><span className="text-gray-500 dark:text-gray-400">Valor Nominal:</span> <strong>{formatBRL(titleDebt.amount)}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">$ Recebido:</span> <strong className="text-green-600">{formatBRL(titleDebt.paid_amount || '0')}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Natureza:</span> <strong>{referenceColumn(titleDebt.business_reference)}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Vencimento:</span> <strong>{formatDateBR(titleDebt.due_date)}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Status:</span> <BadgeStatus text={badgeText} color={badgeColor} /></div>
                </div>

                {loading ? (
                    <p className="text-center text-gray-500 py-4">Carregando...</p>
                ) : movements.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">Nenhuma movimentação encontrada</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-slate-700">
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                <th className="px-3 py-2">#</th>
                                <th className="px-3 py-2">Título</th>
                                <th className="px-3 py-2 text-center">
                                    {referenceColumn(titleDebt.business_reference)}
                                </th>
                                <th className="px-3 py-2 text-center">Tipo</th>
                                <th className="px-3 py-2 text-right">Valor</th>
                                <th className="px-3 py-2">Data</th>
                                <th className="px-3 py-2">Fonte</th>
                                <th className="px-3 py-2">Fatura</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movements.map((m: any) => (
                                <tr key={m.id} className="border-t dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700">
                                    <td className="px-3 py-2 text-xs font-mono text-gray-500" title={m.charge_id || ''}>
                                        {m.charge_id ? String(m.charge_id).slice(0, 8) : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-xs font-medium" title={m.title_debt_id || ''}>{titleLabel(m)}</td>
                                    <td className="px-3 py-2 text-center">{m.title_installment_number ?? '-'}</td>
                                    {/* o selo carrega a cor da família de pagamento: liquidação
                                        verde, parcial olive, pós-liquidação teal */}
                                    <td className="px-3 py-2 text-center whitespace-nowrap">
                                        <BadgeStatus
                                            text={movementTypeLabels[m.movement_type] || m.movement_type}
                                            color={movementTypeColors[m.movement_type] || 'gray'}
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium">{formatBRL(m.amount)}</td>
                                    <td className="px-3 py-2">{formatDateBR(m.movement_date)}</td>
                                    <td className="px-3 py-2">{m.payment_source || '-'}</td>
                                    <td className="px-3 py-2 text-xs font-mono text-gray-500" title={m.invoice_id || ''}>
                                        {m.invoice_id ? String(m.invoice_id).slice(0, 8) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default MovementsModal;
